/**
 * @fileoverview Componente Molecule para mostrar resumen de una orden de cliente en el Inbox.
 * @component CustomerOrderItemMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.0.0
 *
 * @description
 * Componente optimizado (React.memo) que muestra información clave de una orden:
 * - Número secuencial, fecha, método de entrega, monto, estado y estado de pago.
 * - Usa hooks especializados para separar lógica de datos y acciones.
 * - Traducción internacionalizada con i18n.
 * - Cumple Atomic Design (Molecule) y arquitectura de capas Mercately.
 */
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import IconMC from "../../../components/IconMC";
import CustomerOrderStatusSelectorMC from "./CustomerOrderStatusSelectorMC";
import CustomerOrderPaymentStatusSelectorMC from "./CustomerOrderPaymentStatusSelectorMC";
import CustomerOrderDeliveryStatusSelectorMC from "./CustomerOrderDeliveryStatusSelectorMC";
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import useCustomerOrderItemData from "../../../hooks/inbox/data/useCustomerOrderItemData";
import useCustomerOrderItemActions from "../../../hooks/inbox/actions/useCustomerOrderItemActions";

/**
 * CustomerOrderItemMC - Componente para mostrar resumen de una orden de cliente en el Inbox.
 */
const CustomerOrderItemMC = memo(({ order, openModalOrder, updateOrderInList }) => {
  // Traducción
  const { t } = useTranslation('InboxMC');
  // Data Layer: datos derivados y formateados
  const { deliveryMethodIcon, createdAt, monto } = useCustomerOrderItemData(order);
  // Actions Layer: handlers de negocio
  const { viewOrderDetail, handleOpenEditOrderModal } = useCustomerOrderItemActions({ order, openModalOrder });

  // Debug tracking de dependencias críticas
  useTraceUpdate('CustomerOrderItemMC', {
    orderId: order?.id,
    orderStatus: order?.status,
    paymentStatus: order?.payment_status,
    shopifyId: order?.shopify_id,
    total: order?.total
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper">
      <div className="tw-border tw-rounded-xl tw-px-4 tw-py-3 tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-justify-between tw-text-gray-950 tw-mb-2">
          <div className="tw-text-left tw-text-xs tw-font-medium">
            <span>{`${t('orders.title')} #`}</span>
            <span>{order.sequential_number}</span>
          </div>
          <div className="tw-flex tw-items-center">
            <IconMC name="eye-outline" size="base" className="tw-mr-1" onClick={viewOrderDetail} />
            {!order.shopify_id && order.status !== 'canceled' && (
              <IconMC name="edit-outline" size="base" onClick={handleOpenEditOrderModal} />
            )}
          </div>
        </div>
        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span>{t('event.created_at')}</span>
          <span className="tw-ml-2">{createdAt}</span>
        </div>
        <div className="tw-text-xs tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
          <span>{t('orders.delivery')}</span>
          <img src={deliveryMethodIcon} className="tw-w-5 tw-h-5 tw-ml-2" />
        </div>
        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span>{t('orders.amount')}</span>
          <span className="tw-ml-2">{monto}</span>
        </div>
        <div className="tw-text-xs tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
          <span>{t('orders.status')}</span>
          <span className="tw-ml-2">
            <CustomerOrderStatusSelectorMC order={order} updateOrderInList={updateOrderInList} />
          </span>
        </div>
        <div className="tw-text-xs tw-text-gray-700">
          <span>{t('orders.payment_status')}</span>
          <span className="tw-ml-2">
            <CustomerOrderPaymentStatusSelectorMC order={order} updateOrderInList={updateOrderInList} />
          </span>
        </div>
        <div className="tw-text-xs tw-text-gray-700 tw-mt-2">
          <span>{t('orders.delivery_status')}</span>
          <span className="tw-ml-2">
            <CustomerOrderDeliveryStatusSelectorMC order={order} updateOrderInList={updateOrderInList} />
          </span>
        </div>
      </div>
    </div>
  );
});

CustomerOrderItemMC.displayName = 'CustomerOrderItemMC';

export default CustomerOrderItemMC;
